import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { StatsCard } from '../components/StatsCard';
import { AdminReviewCard } from '../components/AdminReviewCard';
import { AdminReviewFilters } from '../components/AdminReviewFilters';
import { ReviewActionModal } from '../components/ReviewActionModal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Review, ReviewFilterType } from '../types';
import { ROUTES } from '../constants';
import { useAuth } from '../context/AuthContext';
import {
  getAdminReviewsList,
  approveReview,
  rejectReview,
  deleteReview,
} from '../services/api';
import { AlertCircle, CheckCircle, XCircle, Flag, ChevronLeft, ChevronRight } from 'lucide-react';

type ActionType = 'approve' | 'reject' | 'delete';

const PAGE_LIMIT = 10;

export const AdminReviewsPage: React.FC = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<ReviewFilterType>('flagged');
  const [searchQuery, setSearchQuery] = useState('');
  const [providerIdFilter, setProviderIdFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<{
    type: ActionType;
    reviewId: number;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchReviews = useCallback(async (currentPage: number) => {
    setLoading(true);
    setError('');
    try {
      const response = await getAdminReviewsList({
        filterType: activeFilter,
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(providerIdFilter.trim() && !isNaN(parseInt(providerIdFilter)) && {
          providerId: parseInt(providerIdFilter),
        }),
        ...(userIdFilter.trim() && !isNaN(parseInt(userIdFilter)) && {
          userId: parseInt(userIdFilter),
        }),
        page: currentPage,
        limit: PAGE_LIMIT,
      });

      if (response.success) {
        setReviews(response.reviews);
        setTotal(response.total);
      } else {
        setError(response.error || 'Failed to fetch reviews');
      }
    } catch (err) {
      setError('An error occurred while fetching reviews');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchQuery, providerIdFilter, userIdFilter]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter, searchQuery, providerIdFilter, userIdFilter]);

  useEffect(() => {
    fetchReviews(page);
  }, [fetchReviews, page]);

  const handleAction = (type: ActionType, reviewId: number) => {
    setCurrentAction({ type, reviewId });
    setModalOpen(true);
  };

  const handleConfirmAction = async (reason?: string) => {
    if (!currentAction) return;

    setActionLoading(true);
    setSuccessMessage('');

    try {
      let response;
      switch (currentAction.type) {
        case 'approve':
          response = await approveReview(currentAction.reviewId);
          break;
        case 'reject':
          if (!reason) {
            alert('Rejection reason is required');
            setActionLoading(false);
            return;
          }
          response = await rejectReview(currentAction.reviewId, reason);
          break;
        case 'delete':
          response = await deleteReview(currentAction.reviewId);
          break;
      }

      if (response.success) {
        setSuccessMessage(response.message);
        await fetchReviews(page);
        setModalOpen(false);
        setCurrentAction(null);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        alert(response.message || 'Action failed');
      }
    } catch (err) {
      alert('Failed to perform action. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return <Navigate to={ROUTES.HOME_PAGE} replace />;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
  const pageEnd = Math.min(page * PAGE_LIMIT, total);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header theme="blue" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Review Moderation</h1>
          <p className="text-gray-600 mt-2">
            Manage and moderate user reviews requiring approval
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Flagged Reviews"
            value={activeFilter === 'flagged' ? total : 0}
            icon={Flag}
            color="yellow"
          />
          <StatsCard
            title="Pending"
            value={activeFilter === 'pending' ? total : 0}
            icon={AlertCircle}
            color="yellow"
          />
          <StatsCard
            title="Approved"
            value={activeFilter === 'approved' ? total : 0}
            icon={CheckCircle}
            color="green"
          />
          <StatsCard
            title="Rejected"
            value={activeFilter === 'rejected' ? total : 0}
            icon={XCircle}
            color="red"
          />
        </div>

        <div className="mb-6">
          <AdminReviewFilters
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            providerIdFilter={providerIdFilter}
            onProviderIdChange={setProviderIdFilter}
            userIdFilter={userIdFilter}
            onUserIdChange={setUserIdFilter}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Reviews</h2>
            <span className="text-sm text-gray-500">
              {total === 0
                ? 'No reviews found'
                : `Showing ${pageStart}–${pageEnd} of ${total} reviews`}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews found</h3>
              <p className="text-gray-600">
                {searchQuery || providerIdFilter || userIdFilter
                  ? 'Try adjusting your filters'
                  : 'There are no reviews matching the selected criteria'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {reviews.map((review) => (
                <div key={review.reviewId} className="p-6">
                  <AdminReviewCard
                    review={review}
                    onApprove={(reviewId) => handleAction('approve', reviewId)}
                    onReject={(reviewId) => handleAction('reject', reviewId)}
                    onDelete={(reviewId) => handleAction('delete', reviewId)}
                    loading={actionLoading}
                  />
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        disabled={loading}
                        className={`w-9 h-9 text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed ${
                          pageNum === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {currentAction && (
        <ReviewActionModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setCurrentAction(null);
          }}
          onConfirm={handleConfirmAction}
          action={currentAction.type}
          reviewId={currentAction.reviewId}
          loading={actionLoading}
        />
      )}
    </div>
  );
};
